"use client";

import { useState, useEffect } from "react";
import { Box, Typography, Button, Paper, IconButton, CircularProgress, Snackbar } from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import MigrationTable from "../components/specific/MigrationTable/MigrationTable";
import AdminSidebar from "../components/specific/AdminSidebar/AdminSidebar";
import { useMigrations } from "../hooks/useMigrations";
import { mapApiMigrationToTableMigration } from "../utils/migration-mappers";
import { useNavigate } from "react-router-dom";
import { useAccountId } from "../hooks/useAccountId";
import type { Migration } from "../types/migrations";

export default function MigrationList() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);

  // Get account ID using the hook
  const accountId = useAccountId();
  const [accountIdLoading, setAccountIdLoading] = useState(!accountId);

  // Update account ID loading state
  useEffect(() => {
    if (accountId) {
      setAccountIdLoading(false);
    }
  }, [accountId]);

  // Use migrations hook
  const {
    migrations: apiMigrations,
    loading,
    error,
    refetch,
  } = useMigrations({
    accountId: accountId || "",
    autoFetch: !!accountId, // Only auto-fetch if accountId exists
  });

  // Convert API migrations to table format
  const [tableMigrations, setTableMigrations] = useState<Migration[]>([]);

  // Update table migrations when API migrations change
  useEffect(() => {
    if (apiMigrations && apiMigrations.length > 0) {
      // Map each API migration to the table migration format
      const mappedMigrations = apiMigrations.map(mapApiMigrationToTableMigration);
      setTableMigrations(mappedMigrations);
    }
  }, [apiMigrations]);

  // Show error snackbar if there's an error
  useEffect(() => {
    if (error) {
      setErrorSnackbarOpen(true);
    }
  }, [error]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleErrorSnackbarClose = () => {
    setErrorSnackbarOpen(false);
  };

  // Handle navigation to migration process
  const handleGoToProcess = () => {
    navigate("/migrate");
  };

  // Handle navigation to migration details
  const handleViewMigration = (migrationId: string) => {
    navigate(`/migration/${migrationId}`);
  };

  // If no accountId, show a message
  if (!accountId) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="error">
              Account ID not found. Please log in again.
            </Typography>
            <Button variant="contained" onClick={() => navigate("/login")} sx={{ mt: 2 }}>
              Go to login
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // If loading accountId, show loading indicator
  if (accountIdLoading) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />
        <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading account information...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Admin Sidebar */}
      <AdminSidebar open={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main content */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Page content */}
        <Box sx={{ flexGrow: 1, overflow: "auto", bgcolor: "#f5f5f5" }}>
          <Box sx={{ p: 3 }}>
            {/* Modernization Header */}
            <Paper sx={{ p: 2, mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h5" component="h1">
                Migration Management
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToProcess}
                sx={{
                  bgcolor: "#1775e0",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: "medium",
                  px: 3,
                }}
              >
                Create New Migration
              </Button>
            </Paper>

            {/* Migrations Table */}
            <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="h6">List of Migrations</Typography>
                <Box>
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
                  </IconButton>
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                  <IconButton>
                    <FilterListIcon />
                  </IconButton>
                </Box>
              </Box>
              <MigrationTable migrations={tableMigrations} isLoading={loading} onViewMigration={handleViewMigration} />
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
        message={error || "Error loading migrations"}
        action={
          <IconButton size="small" color="inherit" onClick={handleErrorSnackbarClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}
